import {
  GraphQLGlobalTypes,
  TableRowDataFunctions_getRowDataWithMaxCountsForTable,
  TablesByTagQueryGql,
  TablesDefaultDirections,
  TablesDefaultSortField,
  TablesDisplayGridRowData,
  TablesExplorerQueryGrid_mapResultNodeWithTagFilter,
  TablesExplorerQueryGrid_QueryResultListNode,
  TablesExplorerQueryGrid_QueryResultListNodeWithTagFilter,
  TablesExplorerQueryGrid_QueryResults,
  TablesExplorerQueryGrid_QueryResultsWithTagFilter,
  TablesExplorerQueryGrid_QueryVariables,
  TablesExplorerQueryGrid_QueryVariablesWithTagFilter,
  TablesExplorerQueryGrid_selectConnectionListResult,
  TablesExplorerQueryGrid_selectConnectionListResultWithTagFilter,
  TablesQueryGql,
} from '@tableau/data-catalog-client-components';
import { defaultMemoize } from 'reselect';
import { ISliceable } from '../../ts/models/ISliceable';
import { catalogApolloClient } from '../CatalogApolloClient';
import { BaseGraphQLResourceQuery } from './BaseGraphQLResourceQuery';
import { getExplorerGraphQLFilters } from './CatalogFilterClauseHelpers';
import { getSortOrderOrDefault } from './CatalogOrderClauseHelpers';
import { makeSliceableFactoryForGraphQLQuery } from './GraphQLSliceable';

const getDefaultSliceableFactorySingleton = defaultMemoize(() => {
  return makeSliceableFactoryForGraphQLQuery<
    TablesExplorerQueryGrid_QueryResultListNode,
    TablesDisplayGridRowData,
    TablesExplorerQueryGrid_QueryVariables,
    TablesExplorerQueryGrid_QueryResults
    >(
    {
      queryGql: TablesQueryGql,
      selectConnectionListResult: TablesExplorerQueryGrid_selectConnectionListResult,
      mapResultNode: TableRowDataFunctions_getRowDataWithMaxCountsForTable,
    },
    catalogApolloClient
  );
});

const getTagFilteredSliceableFactorySingleton = defaultMemoize(() => {
  return makeSliceableFactoryForGraphQLQuery<
    TablesExplorerQueryGrid_QueryResultListNodeWithTagFilter,
    TablesDisplayGridRowData,
    TablesExplorerQueryGrid_QueryVariablesWithTagFilter,
    TablesExplorerQueryGrid_QueryResultsWithTagFilter
    >(
    {
      queryGql: TablesByTagQueryGql,
      selectConnectionListResult: TablesExplorerQueryGrid_selectConnectionListResultWithTagFilter,
      mapResultNode: TablesExplorerQueryGrid_mapResultNodeWithTagFilter,
    },
    catalogApolloClient
  );
});

export class TablesGraphQLResourceQuery
  extends BaseGraphQLResourceQuery<TablesDisplayGridRowData> {

  protected makeSliceable(): ISliceable<TablesDisplayGridRowData> {
    const orderBy = getSortOrderOrDefault(this.getOrder(), TablesDefaultSortField, TablesDefaultDirections);
    const filterParameters = getExplorerGraphQLFilters(this.getFilters());

    const partialQueryVariables = {
      orderBy: orderBy as GraphQLGlobalTypes.DatabaseTableSortOrder,
      textSearch: filterParameters.search,
      hasActiveWarning: filterParameters.hasActiveWarning,
      isCertified: filterParameters.isCertified,
    };

    const sliceable = filterParameters.tags
      ? getTagFilteredSliceableFactorySingleton()({ tagName: filterParameters.tags, ...partialQueryVariables })
      : getDefaultSliceableFactorySingleton()(partialQueryVariables);

    return sliceable;
  }
}
